import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import ConfirmationModal from "components/common/ConfirmationMessage";
import { ListLayout } from "components/common/ListLayout";
import TableHeaderItem from "components/common/ListLayout/TableHeaderItem";
import { usePermissions } from "layouts/Admin";
import { convertTimeToUTC } from "helpers/convertTimeToUTC";

// Redux Slice Poeta
import {
  fetchPoetas,
  poetasSelector,
  removeMultiplePoetas,
} from "slices/poetaSlice";

// Add Form Component
import AddPoeta from "components/poeta/AddPoetaForm";
import { convertTimeToUTCFormatted } from "helpers/convertTimeToUTC";

function PoetaList() {
  const dispatch = useDispatch();
  const { checkPermission } = usePermissions();

  const {
    poetas,
    last_page: totalPages,
    loader,
    total,
    current_page,
    per_page: pageLength,
  } = useSelector(poetasSelector);

  const [addForm, setAddForm] = useState({ show: false, item: {} });
  const refAddButton = React.useRef(null);

  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    sortBy: "project",
    sortDirection: "asc",
  });

  React.useEffect(() => {
    dispatch(fetchPoetas());
  }, []);

  const handleAddPopUp = (item) => {
    setAddForm({ show: true, item });
  };

  const submitCallback = (status, message) => {
    if (status) {
      toast.success(message, { className: "successToast" });
      setSelectedItems([]);
      setSelectAll(false);
      dispatch(fetchPoetas());
      hideConfirmDialog();
    } else {
      toast.error(message, { className: "errorToast" });
    }
  };

  const showConfirmDialog = () => setDeleteModal(true);
  const hideConfirmDialog = () => setDeleteModal(false);

  const handleRemoveItems = () => {
    dispatch(removeMultiplePoetas({ ids: selectedItems }, submitCallback));
    setDeleteModal(false);
  };

  const handleChange = (e) => {
    const id = e.target.dataset.value;
    setSelectedItems(
      selectedItems.includes(id)
        ? selectedItems.filter((item) => item !== id)
        : [...selectedItems, id]
    );
  };

  const handleToggleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
      setSelectAll(false);
      return;
    }
    setSelectAll(true);
    setSelectedItems(poetas?.map((p) => p.id.toString()));
  };

  const handleClose = () => {
    setAddForm({ show: false, item: {} });
  };

  //#region TABLE HEAD
  const renderTableHeader = React.useCallback(() => {
    const callBack = (dir, sortBy) => {
      setSortConfig({ sortBy, sortDirection: dir });
      dispatch(
        fetchPoetas(current_page, {
          sortBy,
          sortDirection: dir,
          search: searchTerm,
        })
      );
    };

    return (
      <tr>
        <th className="col-1">
          <div className="row_check">
            <span>#</span>
            {checkPermission("Delete POETA") && (
              <label className="custom-checkbox checkbox">
                <input
                  type="checkbox"
                  name="select-all"
                  checked={selectAll}
                  onChange={handleToggleSelectAll}
                />
                <span className="checkmark"></span>
              </label>
            )}
          </div>
        </th>
        <th className="col">
          <TableHeaderItem
            title="Project"
            sortBy="project"
            sortDirection={sortConfig.sortDirection}
            callBack={callBack}
          />
        </th>
        <th className="col">Task</th>
        <th className="col">Expense Type Account</th>
        <th className="col">Organization</th>
        <th className="col">Award</th>
        <th className="col">
          <TableHeaderItem
            title="Created At"
            sortBy="created_at"
            sortDirection={sortConfig.sortDirection}
            callBack={callBack}
          />
        </th>
        {checkPermission("Edit POETA") && <th className="col">Actions</th>}
      </tr>
    );
  }, [selectAll, searchTerm, sortConfig, current_page]);
  //#endregion

  //#region TABLE BODY
  const renderTableContent = React.useCallback(() => {
    return poetas?.map((p, index) => (
      <PoetaItem
        key={p.id}
        poeta={p}
        index={index}
        selectedItems={selectedItems}
        handleChange={handleChange}
        handleAddPopUp={handleAddPopUp}
        current_page={current_page}
        pageLength={pageLength}
        checkPermission={checkPermission}
      />
    ));
  }, [poetas, selectedItems]);
  //#endregion

  return (
    <ListLayout
      heading="POETA"
      buttonTitle="Add POETA"
      renderTableHeader={renderTableHeader}
      renderTableContent={renderTableContent}
      totalPages={totalPages}
      addButtonDisabled={!checkPermission("Create POETA")}
      fetchList={fetchPoetas}
      selectedItems={selectedItems}
      handleRemoveItems={handleRemoveItems}
      renderAddForm={(callback) => (
        <AddPoeta
          item={addForm.item}
          show={addForm.show}
          onClose={handleClose}
          callback={callback}
          statusPerm={checkPermission("Status Poeta")}
        />
      )}
      refAddButton={refAddButton}
      refAddPopUp="#poetaModal"
      addForm={addForm}
      showConfirmDialog={showConfirmDialog}
      handleAddPopUp={handleAddPopUp}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      loader={loader}
      disableBootstrap={true}
      noOfItems={total}
      searchPlaceholder="Search Poeta..."
    >
      {deleteModal && (
        <ConfirmationModal
          onClose={hideConfirmDialog}
          isOpen={deleteModal}
          onConfirm={handleRemoveItems}
        />
      )}
    </ListLayout>
  );
}

const PoetaItem = ({
  poeta,
  index,
  selectedItems,
  handleChange,
  handleAddPopUp,
  current_page,
  pageLength,
  checkPermission,
}) => {
  const [status, setStatus] = useState(poeta?.status === "active" ? 1 : 0);

  React.useEffect(() => {
    setStatus(poeta?.status === "active" ? 1 : 0);
  }, [poeta]);

  const getBackgroundStyle = (status) =>
    status === 0 ? { background: "#f4f4f4" } : {};

  const serial = useMemo(
    () => index + 1 + (current_page - 1) * pageLength,
    [current_page, pageLength]
  );


  return (
    <tr>
      <td style={getBackgroundStyle(status)}>
        <div className="row_check">
          <span>{serial}</span>
          {checkPermission("Delete POETA") && (
            <label className="custom-checkbox checkbox">
              <input
                type="checkbox"
                name="select-all"
                data-value={poeta.id}
                checked={selectedItems.includes(poeta.id.toString())}
                onChange={handleChange}
              />
              <span className="checkmark"></span>
            </label>
          )}
        </div>
      </td>
      <td style={getBackgroundStyle(status)}>{poeta.project}</td>
      <td style={getBackgroundStyle(status)}>{poeta.task}</td>
      <td style={getBackgroundStyle(status)}>{poeta.expense_type_account}</td>
      <td style={getBackgroundStyle(status)}>{poeta.org}</td>
      <td style={getBackgroundStyle(status)}>{poeta.award}</td>
        <td style={getBackgroundStyle(status)}>
          {convertTimeToUTCFormatted(poeta.created_at)}
        </td>

      {checkPermission("Edit POETA") && (
        <td style={getBackgroundStyle(status)}>
          <div className="row_check">
            <button
              className="table_edit"
              onClick={() => handleAddPopUp({ ...poeta, status })}
            >
              Edit
            </button>
          </div>
        </td>
      )}
    </tr>
  );
};

export default PoetaList;
